import React, { useState } from 'react';
import { Switch, Select, Tooltip, Icon, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { DESIGN_AREA_SETTINGS_TAB } from '../../../SidebarMenu/translationValues';
import { useBusinessContractSettings } from '../hooks/useBusinessContractSettings';
import BusinessContractConfirmationModal from './BusinessContractConfirmationModal';
import {
  BusinessContractContainer,
  SettingRow,
  ToggleLabel,
  ContractSelectContainer,
  SelectLabel,
  DescriptionText,
  InfoIcon,
  LoadingContainer,
} from './BusinessContractSection.styles';

const { Option } = Select;
const NAMESPACES = ['DESIGN_AREA_SETTINGS_TAB'];

const BusinessContractSection = ({ readOnly }) => {
  const { t } = useTranslation(NAMESPACES);
  const {
    isDataMappingEnabled,
    selectedContractId,
    contracts,
    loading,
    saving,
    toggleDataMapping,
    selectContract,
    saveMapping,
    cancelChanges,
    hasExistingMapping,
  } = useBusinessContractSettings();

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(null);
  const [pendingContractId, setPendingContractId] = useState(null);

  const handleToggleChange = (checked) => {
    if (readOnly) return;
    
    // If enabling for the first time (no existing mapping), just enable the dropdown
    if (checked && !hasExistingMapping && !isDataMappingEnabled) {
      toggleDataMapping();
      return;
    }
    
    // If disabling or changing existing mapping, show confirmation
    if (hasExistingMapping) {
      setPendingToggle(checked);
      setPendingContractId(checked ? selectedContractId : null);
      setShowConfirmation(true);
    } else {
      // Disabling when there's no existing mapping - just toggle
      toggleDataMapping();
    }
  };

  const handleContractChange = async (contractId) => {
    if (readOnly) return;
    
    // If this is the first time selecting a contract (no existing mapping), save directly
    if (!hasExistingMapping) {
      // First enable mapping if not already enabled
      if (!isDataMappingEnabled) {
        toggleDataMapping();
      }
      // Then select the contract and save
      selectContract(contractId);
      // Wait a bit for state to update, then save
      setTimeout(async () => {
        await saveMapping(true, contractId);
      }, 100);
      return;
    }
    
    // If changing an existing contract, show confirmation
    if (contractId !== selectedContractId) {
      setPendingContractId(contractId);
      setPendingToggle(true);
      setShowConfirmation(true);
    }
  };

  const handleConfirm = async () => {
    // Determine the values to save
    const enabledToSave = pendingToggle !== null ? pendingToggle : isDataMappingEnabled;
    const contractIdToSave = pendingContractId !== null ? pendingContractId : selectedContractId;
    
    // Save with the pending values directly
    const success = await saveMapping(enabledToSave, contractIdToSave);
    if (success) {
      setShowConfirmation(false);
      setPendingToggle(null);
      setPendingContractId(null);
    }
  };

  const handleCancel = () => {
    // Just close the modal without making changes
    setShowConfirmation(false);
    setPendingToggle(null);
    setPendingContractId(null);
  };

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="small" />
        <span style={{ marginLeft: 8 }}>
          {t(
            DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_LOADING?.key || 'Loading contracts...',
            DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_LOADING?.value || 'Loading contracts...'
          )}
        </span>
      </LoadingContainer>
    );
  }

  return (
    <>
      <BusinessContractContainer>
        <SettingRow>
          <ToggleLabel>
            {t(
              DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ENABLE_DATA_MAPPING?.key || 'Enable data mapping',
              DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ENABLE_DATA_MAPPING?.value || 'Enable data mapping'
            )}
            <Tooltip
              title={t(
                DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ENABLE_DATA_MAPPING_TOOLTIP?.key || 'Enable data mapping tooltip',
                DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ENABLE_DATA_MAPPING_TOOLTIP?.value || 'Enable data mapping tooltip'
              )}
            >
              <InfoIcon>
                <Icon type="info-circle" style={{ fontSize: '12px' }} />
              </InfoIcon>
            </Tooltip>
          </ToggleLabel>
          <Switch
            checked={pendingToggle !== null ? pendingToggle : isDataMappingEnabled}
            onChange={handleToggleChange}
            disabled={readOnly || saving}
          />
        </SettingRow>

        <ContractSelectContainer>
          <SelectLabel>
            {t(
              DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_SELECT_CONTRACT?.key || 'Select business data contract',
              DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_SELECT_CONTRACT?.value || 'Select business data contract'
            )}
            <Tooltip
              title={t(
                DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_SELECT_CONTRACT_TOOLTIP?.key || 'Select contract tooltip',
                DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_SELECT_CONTRACT_TOOLTIP?.value || 'Select contract tooltip'
              )}
            >
              <InfoIcon>
                <Icon type="info-circle" style={{ fontSize: '12px' }} />
              </InfoIcon>
            </Tooltip>
          </SelectLabel>
          <Select
            value={pendingContractId !== null ? (pendingContractId || undefined) : (selectedContractId || undefined)}
            onChange={handleContractChange}
            placeholder={t(
              DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_CHOOSE_PLACEHOLDER?.key || 'Choose a contract...',
              DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_CHOOSE_PLACEHOLDER?.value || 'Choose a contract...'
            )}
            disabled={!(pendingToggle !== null ? pendingToggle : isDataMappingEnabled) || readOnly || saving}
            style={{ width: '100%' }}
            showSearch
            filterOption={(input, option) =>
              option.props.children?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {contracts.map(contract => (
              <Option key={contract.id} value={contract.id}>
                {contract.name}
              </Option>
            ))}
          </Select>
          <DescriptionText>
            {t(
              DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_DESCRIPTION?.key || 
              'Selected contract will transform incoming data to match your PDF template structure',
              DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_DESCRIPTION?.value || 
              'Selected contract will transform incoming data to match your PDF template structure'
            )}
          </DescriptionText>
        </ContractSelectContainer>
      </BusinessContractContainer>

      <BusinessContractConfirmationModal
        visible={showConfirmation}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        loading={saving}
      />
    </>
  );
};

export default BusinessContractSection;

